import { useCallback, useMemo, useState } from "react";
import { Navigation, WarningAmber } from "@mui/icons-material";
import { CircularProgress, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Marker as MapGLMarker } from "react-map-gl/mapbox";

import type { ParametersLocation } from "@/lib/api";
import { dontForwardProps } from "@/lib/styled";
import { resolveThresholdMarkerTextColor } from "@/components/graph/threshold-colors";
import { useTranslation } from "@/lib/i18n";

import {
  type LocationMarkerLoadState,
  MARKER_NO_DATA,
  type MarkerDisplayState,
  type MarkerRowCell,
  shouldShowMarker,
} from "./marker-display-state";

type MarkerState = "active" | "hover";

type MarkerDotPalette = { base: string; hover: string; ring: string };

type StationMarkerProps = {
  location: ParametersLocation;
  loadState: LocationMarkerLoadState;
  displayState: MarkerDisplayState;
  supportsAnyConfiguredChartParameter: boolean;
  selected: boolean;
  onSelect: () => void;
  /**
   * Optional stacking priority; lower index = higher priority = higher zIndex,
   * so higher-priority stations render on top when markers overlap. When
   * omitted, zIndex matches the previous selected/hover-only behaviour.
   */
  priorityIndex?: number;
  /**
   * When true the marker is decluttered (overlaps a higher-priority marker):
   * the value pill is kept mounted (so it stays measurable) but hidden, and a
   * small light-blue dot marks the station's location instead.
   */
  hidden?: boolean;
  /**
   * When true the marker has no data at all (every slot is a dash) and is not
   * selected: it is shown permanently as a neutral-gray dot rather than a pill,
   * regardless of overlap. Takes visual precedence over the declutter dot.
   */
  emptyNoData?: boolean;
  /** Registers the measurable pill element for overlap detection. */
  registerRef?: (element: HTMLElement | null) => void;
};

/* Compact map font-size shrinks on small maps via the `map` container query
   (MapStack sets container-name: map; container-type: size). */
const COMPACT_MOBILE_BREAKPOINT = 480;

/**
 * Compact marker: black value text sits directly on the map (no background),
 * traced with a white outline so it stays legible over any imagery. Hover/active
 * bump the weight. The font shrinks on small maps via the `map` container query.
 *
 * Layout is the wind arrow (when present) ahead of a top-aligned column of unit
 * rows: `alignItems: flex-start` keeps the arrow level with the first row.
 */
const CompactMarker = styled("div", {
  shouldForwardProp: dontForwardProps("state"),
})<{ state?: MarkerState }>(({ state }) => ({
  alignItems: "flex-start",
  color: "var(--black)",
  cursor: "pointer",
  display: "flex",
  fontSize: 13,
  fontWeight: state ? 700 : 600,
  gap: 3,
  justifyContent: "center",
  lineHeight: 1.15,
  padding: "2px 4px",
  /* Selected marker gets a translucent, slightly-blue rounded backdrop with a
     solid dark-blue border so it stands out from the other background-less
     markers. Radius + a transparent border are set unconditionally (harmless
     without a background) so toggling selection causes no reflow. */
  borderRadius: 6,
  border: "1px solid",
  borderColor: state === "active" ? "#1f5fa8" : "transparent",
  backgroundColor: state === "active" ? "rgba(43, 144, 234, 0.22)" : "transparent",
  textAlign: "left",
  /* Full white outline keeps black text legible on any map background. */
  textShadow: [
    "-1px -1px 0 #fff",
    "1px -1px 0 #fff",
    "-1px 1px 0 #fff",
    "1px 1px 0 #fff",
    "-2px 0 0 #fff",
    "2px 0 0 #fff",
    "0 -2px 0 #fff",
    "0 2px 0 #fff",
    "-2px -2px 1px #fff",
    "2px 2px 1px #fff",
    "0 0 3px #fff",
    "0 0 4px #fff",
  ].join(", "),
  /* Give icons (e.g. the wind-direction arrow) the same white outline as the
     text — text-shadow does not affect SVGs, so stack directional white
     drop-shadows to trace their edge. */
  "& svg": {
    filter: [
      "drop-shadow(1px 0 0 #fff)",
      "drop-shadow(-1px 0 0 #fff)",
      "drop-shadow(0 1px 0 #fff)",
      "drop-shadow(0 -1px 0 #fff)",
      "drop-shadow(1px 1px 0 #fff)",
      "drop-shadow(-1px -1px 0 #fff)",
      "drop-shadow(1px -1px 0 #fff)",
      "drop-shadow(-1px 1px 0 #fff)",
    ].join(" "),
  },
  [`@container map (max-width: ${COMPACT_MOBILE_BREAKPOINT}px)`]: {
    fontSize: 11,
  },
}));

/** Vertical stack of unit rows, left-aligned beside the wind arrow. */
const MarkerRows = styled("div")({
  alignItems: "flex-start",
  display: "flex",
  flexDirection: "column",
  rowGap: 2,
});

/** A single unit group: its values laid out horizontally. */
const MarkerRow = styled("div")({
  alignItems: "center",
  display: "flex",
  gap: 6,
});

/** Minimal value cell: no min-height / bubble padding from the shared marker. */
const CompactMarkerValue = styled("span")({
  alignItems: "center",
  display: "inline-flex",
  gap: 4,
  whiteSpace: "nowrap",
});

/**
 * The "pill" side of a marker (wind arrow + value rows / spinner). Fades out when
 * the marker collapses to a dot and fades back in when it re-expands. It keeps
 * its box (opacity, not display/visibility) so the full pill stays measurable for
 * overlap detection while faded. Holds the arrow-ahead-of-rows flex layout.
 */
const MarkerPillContent = styled("div", {
  shouldForwardProp: dontForwardProps("faded"),
})<{ faded: boolean }>(({ faded }) => ({
  alignItems: "flex-start",
  display: "flex",
  gap: 3,
  opacity: faded ? 0 : 1,
  transition: "opacity 0.2s ease",
}));

/**
 * Sky-blue tone for a marker temporarily collapsed because it overlaps a
 * higher-priority marker.
 */
const DECLUTTER_PALETTE: MarkerDotPalette = {
  base: "#5eb3f6",
  hover: "#2b90ea",
  ring: "rgba(43, 144, 234, 0.35)",
};

/**
 * Neutral-gray tone for a marker that has no data at all (every slot a dash),
 * shown permanently as a dot. Darker than the map so it reads without relying on
 * a hue, and distinct from the sky-blue declutter dot.
 */
const NO_DATA_PALETTE: MarkerDotPalette = {
  base: "#BFBFBF",
  hover: "#A6A6A6",
  ring: "rgba(166, 166, 166, 0.5)",
};

/**
 * Shown in place of a full pill so the station's location is still visible as a
 * small dot. Sits at the marker's center (the geographic anchor point). The pill
 * content fades to `opacity: 0` (keeping its box) rather than unmounting, so the
 * full pill stays measurable for overlap detection without shrinking to the dot
 * (which would flicker). The `palette` selects the color.
 */
const HiddenMarkerDot = styled("span", {
  shouldForwardProp: dontForwardProps("palette"),
})<{ palette: MarkerDotPalette }>(({ palette: colors }) => {
  return {
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: colors.base,
    border: "1px solid rgba(255, 255, 255, 0.9)",
    boxShadow: "0 0 2px rgba(0, 0, 0, 0.35)",
    /* Be the interactive hit target for the collapsed marker: the marker sets
       pointer-events:none while collapsed (so the faded pill footprint is not
       clickable), so the dot re-enables just itself and shows a pointer cursor.
       Clicks bubble to the MapGLMarker onClick. */
    pointerEvents: "auto",
    cursor: "pointer",
    transition: "transform 0.12s ease, background-color 0.12s ease, box-shadow 0.12s ease",
    /* Enlarge the clickable/hover area well beyond the small visible dot: the map
       cursor is a centered hand (no fine tip), so a bigger transparent target
       makes dots easy to hit. The visible dot stays 8px. */
    "&::before": {
      content: '""',
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: 28,
      height: 28,
      borderRadius: "50%",
    },
    /* Visual hover cue that the dot is clickable: grow, brighten and add a ring. */
    "&:hover": {
      transform: "translate(-50%, -50%) scale(1.6)",
      backgroundColor: colors.hover,
      boxShadow: `0 0 0 3px ${colors.ring}, 0 0 3px rgba(0, 0, 0, 0.4)`,
    },
  };
});

/** Wind-from arrow: rotate(degrees - 180) matches the shared marker semantics. */
const CompactWindArrow = styled("span", {
  shouldForwardProp: dontForwardProps("degrees"),
})<{ degrees: number }>(({ degrees }) => ({
  display: "inline-flex",
  transform: `rotate(${degrees - 180}deg) scale(0.7)`,
}));

/**
 * Decluttered markers (rendered as a dot) sit behind every visible marker.
 * Visible markers use a base zIndex of at least 1, so 0 keeps dots underneath.
 */
const HIDDEN_MARKER_Z_INDEX = 0;

/**
 * Selected/hover markers stay above the rest. When a priorityIndex is provided,
 * higher-priority (lower index) markers get a higher base zIndex. With no
 * priorityIndex the values match the previous behaviour exactly. Markers shown
 * as a dot (decluttered or empty no-data) are forced behind all visible markers.
 */
function computeMarkerZIndex(
  state: MarkerState | undefined,
  priorityIndex: number | undefined,
  asDot: boolean,
): number {
  if (asDot) return HIDDEN_MARKER_Z_INDEX;
  if (priorityIndex === undefined) {
    return state ? 500 : 100;
  }
  const base = Math.max(1, 400 - priorityIndex);
  return state ? base + 500 : base;
}

function MarkerParameterCellContent({ cell }: { cell: MarkerRowCell }) {
  const { t } = useTranslation();

  switch (cell.kind) {
    case "unsupported":
    case "noData":
      return MARKER_NO_DATA;
    case "error":
      return (
        <Tooltip
          title={t("map.observed_stations.parameter_load_failed", {
            parameter: cell.parameterLabel,
          })}
        >
          <span>
            <WarningAmber fontSize="small" />
          </span>
        </Tooltip>
      );
    case "stationName":
    case "value":
      return cell.text;
  }
}

export function StationMarker({
  location,
  loadState,
  displayState,
  supportsAnyConfiguredChartParameter,
  selected,
  onSelect,
  priorityIndex,
  hidden = false,
  emptyNoData = false,
  registerRef,
}: StationMarkerProps) {
  const [hovered, setHovered] = useState(false);

  const handleMarkerClick = useCallback(() => {
    onSelect();
  }, [onSelect]);

  const state = useMemo<MarkerState | undefined>(() => {
    if (selected) return "active";
    if (hovered) return "hover";
    return undefined;
  }, [selected, hovered]);

  if (!shouldShowMarker(supportsAnyConfiguredChartParameter)) return null;

  // A marker collapses to a dot when it is decluttered (overlap) or permanently
  // empty (no data). The empty no-data dot takes visual precedence (gray tone).
  const asDot = hidden || emptyNoData;
  const dotPalette = emptyNoData ? NO_DATA_PALETTE : DECLUTTER_PALETTE;

  return (
    <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <MapGLMarker
        latitude={location.coordinates.latitude}
        longitude={location.coordinates.longitude}
        anchor="center"
        style={{
          zIndex: computeMarkerZIndex(state, priorityIndex, asDot),
          // Collapsed markers (dot): make the invisible pill footprint
          // non-clickable. Reliable now that Marker._evaluateOpacity is patched
          // out (see patch-mapbox-marker-opacity). pointer-events is inherited,
          // so the dot re-enables just itself (pointerEvents: auto) and stays the
          // only click target.
          ...(asDot
            ? { pointerEvents: "none" as const }
            : // Note: `undefined` does not work as it doesn't unset the style value.
              // Using `null` works in practice, but we need to silence the error for it.
              // Bug: https://github.com/visgl/react-map-gl/issues/2595
              { pointerEvents: null as never }),
        }}
        onClick={(e) => {
          e.originalEvent.stopPropagation();
          handleMarkerClick();
        }}
      >
        <CompactMarker ref={registerRef} state={state}>
          <MarkerPillContent faded={asDot}>
            {loadState === "loading" ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              displayState.phase === "ready" && (
                <>
                  {displayState.windDegrees !== undefined && (
                    <CompactWindArrow degrees={displayState.windDegrees}>
                      <Navigation fontSize="small" />
                    </CompactWindArrow>
                  )}
                  <MarkerRows>
                    {displayState.rows.map((row, rowIndex) => (
                      <MarkerRow key={`${location.id}-${row.unitKey}-${rowIndex}`}>
                        {row.cells.map((cell, cellIndex) => {
                          const thresholdColor =
                            cell.kind === "value" && cell.thresholdColor
                              ? resolveThresholdMarkerTextColor(cell.thresholdColor)
                              : undefined;
                          return (
                            <CompactMarkerValue
                              key={`${location.id}-${rowIndex}-${cellIndex}`}
                              style={thresholdColor ? { color: thresholdColor } : undefined}
                            >
                              <MarkerParameterCellContent cell={cell} />
                            </CompactMarkerValue>
                          );
                        })}
                      </MarkerRow>
                    ))}
                  </MarkerRows>
                </>
              )
            )}
          </MarkerPillContent>
          {asDot && <HiddenMarkerDot palette={dotPalette} />}
        </CompactMarker>
      </MapGLMarker>
    </div>
  );
}
